import { SetOptional } from 'type-fest';

import { EXPERIENCE_ID } from '../constants/bucket';
import { HUBSPOT_APP } from '../constants/integrations';
import {
  AI_GIFT_SUGGESTION_ENABLED,
  AUTOMATIONS_ENABLED,
  AUTOMATION_CONNECTIONS,
  CRM_SKIP_ALLOWED,
  CUSTOMIZE_ITEMS_PAGE_BODY,
  CUSTOMIZE_ITEMS_PAGE_TITLE,
  DEPARTMENT_BUDGET_ENABLED,
  DIGITAL_BALANCE_LIMIT_ENABLED,
  DIGITAL_BALANCE_LIMIT_VALUE,
  EMAIL_CUSTOM_SENDER,
  HUBSPOT_APP_ENABLED,
  LAST_RUN_ERROR_MESSAGE,
  LAST_RUN_NUMBER_SENDS,
  LAST_RUN_STATUS,
  LAST_RUN_TIMESTAMP,
  MSKU_PAGE_BODY,
  MSKU_PAGE_TITLE,
  PRODUCT_MARKETPLACE_ENABLED,
  RECURRING_ANONYMIZATION,
  RECURRING_ANONYMIZATION_CADENCE_DAYS,
  RECURRING_ANONYMIZATION_ENABLED,
  RECURRING_ANONYMIZATION_LAST_RUN_STATUS,
  REQUIRE_BUSINESS_REASON_FOR_DIRECT_SENDS,
  SIGN_IN_METHOD_SUPPORTS,
  SOB_ENABLED,
  USER_BUDGET_ENABLED,
} from '../constants/organizationsKeys';
import { IAutomatedConnection } from './automations';
import { IHubspotAuthData, TCrmSupports, TEmailSupports } from './integrations';
import { IPromiseCallbacks } from './redux';
import { IApiError } from './shell';
import { IClaimLinkTemplate } from './templates';

//#region Related Enums

export enum OrganizationFilterTypesEnum {
  All,
  Activated,
  Deactivated,
}

export enum EmailLogoTypesEnum {
  Rectangular = 'rectangular',
  Square = 'square',
}

export enum OrganizationResetCustomizationOptionsTypesEnum {
  Email,
  Portal,
  ShippingPage,
}

export enum SigninMethodsEnum {
  Google = 'google.com',
  Outlook = 'microsoft.com',
  Email = 'password',
  All = 'all',
}

//#endregion

//#region State interface

export interface IOrganizationsState {
  current: {
    options: IOrganizationOptions | null;
    departmentList: IDepartment[] | null;
    departmentBudget: IDepartmentBudgetValue | null;
    isDepartmentSummariesLoading: boolean;
    isDepartmentUpdating: boolean;
    name: string | null;
    uid: string | null;
    error: IApiError | null;
    isAutomatedConnectionLoading: boolean;
    defaultTemplate: IClaimLinkTemplate | null;
    isDefaultTemplateLoading: boolean;
  };
  items: IOrganizationItem[] | null;
  // GET /organizations without response_data=all; only for reading uid and name
  shortItems: IOrganizationListItem[] | null;
  organizationDetails: TOrganizationsDetails | null;
  isOrganizationDetailsLoading: boolean;
  isLoading: boolean;
  isOrganizationsPIILoading: boolean;
  runAnonymizationCheck: IRunAnonymizationCheckState;
  error: any;
}

export enum RunAnonymizationCheckPhaseEnum {
  Idle = 'idle',
  Starting = 'starting',
  Running = 'running',
}

export interface IRunAnonymizationCheckState {
  orgId: string | null;
  phase: RunAnonymizationCheckPhaseEnum;
}

//#endregion

//#region Main and related interfaces and types

export interface IOrganizationCustomizationOptions {
  email_use_org_logo: boolean;
  email_accent_color: string;
  email_main_color: string;
  email_logo_type: EmailLogoTypesEnum | '';
  portal_sidebar_color: string;
  portal_sidebar_text_color: string;
  portal_accent_color: string;
  portal_sidebar_accent_color: string;
  portal_use_org_logo: boolean;
  portal_use_cobranding_logo: boolean;
  shipping_page_title: string;
  shipping_page_body: string;
  pick_your_gift_title: string;
  pick_your_gift_body: string;
  [MSKU_PAGE_TITLE]: string;
  [MSKU_PAGE_BODY]: string;
  [CUSTOMIZE_ITEMS_PAGE_TITLE]: string;
  [CUSTOMIZE_ITEMS_PAGE_BODY]: string;
  [EMAIL_CUSTOM_SENDER]?: string;
}

export interface IOrganizationOptions {
  crm_currently_supports: TCrmSupports;
  email_currently_supports: TEmailSupports;
  [AUTOMATION_CONNECTIONS]?: IAutomatedConnection[];
  [SIGN_IN_METHOD_SUPPORTS]: SigninMethodsEnum;
  [CRM_SKIP_ALLOWED]: boolean;
  [SOB_ENABLED]: boolean;
  [USER_BUDGET_ENABLED]: boolean;
  [DEPARTMENT_BUDGET_ENABLED]: boolean;
  [AUTOMATIONS_ENABLED]: boolean;
  [DIGITAL_BALANCE_LIMIT_ENABLED]: boolean;
  [DIGITAL_BALANCE_LIMIT_VALUE]?: number | null;
  [AI_GIFT_SUGGESTION_ENABLED]?: boolean;
  [HUBSPOT_APP_ENABLED]?: boolean;
  [REQUIRE_BUSINESS_REASON_FOR_DIRECT_SENDS]?: boolean;
  [PRODUCT_MARKETPLACE_ENABLED]?: boolean;
  [HUBSPOT_APP]: IHubspotAuthData | null;
  [EXPERIENCE_ID]?: string;
  [RECURRING_ANONYMIZATION]?: IRecurringAnonymization;
}

export interface IRecurringAnonymization {
  [RECURRING_ANONYMIZATION_ENABLED]: boolean;
  [RECURRING_ANONYMIZATION_CADENCE_DAYS]: number | null;
  [RECURRING_ANONYMIZATION_LAST_RUN_STATUS]?: IAnonymizationLastRunStatus | null;
}

export enum LastRunViewEnum {
  None = 'none',
  Completed = 'completed',
  NoMatches = 'noMatches',
  Failed = 'failed',
}

export enum AnonymizationRunStatusEnum {
  Completed = 'completed',
  Failed = 'failed',
}

export interface IAnonymizationLastRunStatus {
  [LAST_RUN_TIMESTAMP]: string;
  [LAST_RUN_NUMBER_SENDS]: number;
  [LAST_RUN_STATUS]: AnonymizationRunStatusEnum;
  [LAST_RUN_ERROR_MESSAGE]?: string | null;
}

export interface IDepartmentBudgetValue {
  available_budget: number;
  used_budget: number;
  budget_currency: string;
}

// TODO: recheck types in a future
//  `org_id` is not a part of response IDepartment model,
//  but it should be sent in the requests to ADD new department only
//  so we need to sort out the types here and make some refactoring inside the forms
export interface IDepartment {
  available_budget?: number;
  org_id?: string;
  used_budget?: number;
  uid: string;
  name: string;
  budget_currency?: string;
}

export interface IDepartmentDetails
  extends SetOptional<IDepartment, 'uid' | 'used_budget' | 'available_budget' | 'budget_currency'> {}

export interface IDelayedShippingPage {
  button_color: string;
  header_color: string;
  logo_url?: string;
}

export interface IShipAddress {
  name: string;
  email: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  residential?: boolean;
}

export interface IOrganizationItem {
  name: string;
  uid: string;
  square_logo_url: string;
  vector_image_url: string;
  order_key: string;
  delayed_shipping_base_url: string;
  shipping_address: IShipAddress;
  delayed_shipping_page: IDelayedShippingPage;
  salesforce_campaign_id: string;
  salesforce_client_id: string;
  salesforce_client_secret_has_value: boolean;
  rybbon_client_id: string;
  rybbon_client_secret: string;
  rybbon_client_secret_has_value: boolean;
  org_departments: IDepartment[];
  org_options: IOrganizationOptions;
  org_customization_options: IOrganizationCustomizationOptions;
  low_inventory_notification_emails: string[] | null;
  // billing_plan_id?: string;
}

// GET /organizations without response_data=all returns only uid and name
export type IOrganizationListItem = Pick<IOrganizationItem, 'uid' | 'name'> & Partial<IOrganizationItem>;

export interface IOrganizationRequestItem {
  square_logo_url: string;
  vector_image_url: string;
  rect_cobranding_logo_url: string;
  square_cobranding_logo_url: string;
  order_key: string;
  name: string;
  delayed_shipping_base_url: string;
  shipping_address: IShipAddress;
  uid: string;
  delayed_shipping_page: IDelayedShippingPage;
  salesforce_campaign_id: string;
  salesforce_client_id: string;
  salesforce_client_secret: string;
  org_options: IOrganizationOptions;
  rybbon_client_id: string;
  rybbon_client_secret: string;
  org_customization_options: IOrganizationCustomizationOptions;
  low_inventory_notification_emails: string[] | null;
}

// Used in OrganizationSidebar
export interface ISimplifiedOrganization
  extends Pick<IOrganizationItem, 'uid' | 'name' | 'org_departments' | 'org_options'> {
  logo_url?: string;
}

export type TOrganizationsDetails = Partial<IOrganizationRequestItem> & Partial<IOrganizationItem>;

export interface IOrganizationFormValues extends IOrganizationRequestItem {
  org_options: IOrganizationOptions;
  org_customization_options: IOrganizationCustomizationOptions;
}

export interface IOrderKeyProps {
  validating: boolean;
  error?: IValidateOrderKeyResponse['errors'] | null;
  warning?: IValidateOrderKeyResponse['warnings'] | null;
  lastChecked: string | null;
}

//#endregion

//#region Request Interfaces and Types

export interface IFetchOrganizationRequestActionPayload {
  responseDataType?: OrganizationsRequestResponseDataTypeEnum;
}

export interface IFetchOrganizationSuccessActionPayload {
  organizations: IOrganizationListItem[];
}

export interface IFetchOrganizationSuccessActionMetadata {
  responseDataType?: OrganizationsRequestResponseDataTypeEnum;
}

export enum OrganizationsRequestResponseDataTypeEnum {
  All = 'all',
}

export interface IAddOrganizationActionPayload extends IPromiseCallbacks {
  values: TOrganizationsDetails;
}
export interface IAddOrganizationActionResponse {
  id: string;
}

export interface IEditOrganizationActionPayload extends IPromiseCallbacks {
  values: TOrganizationsDetails;
  metadata?: { shouldReauthorize: boolean };
}

export interface IAddOrganizationDepartmentActionPayload extends IPromiseCallbacks {
  department: Omit<IDepartmentDetails, 'uid' | 'created_date' | 'used_budget'>;
}

export interface IEditOrganizationDepartmentActionPayload extends IPromiseCallbacks {
  department: IDepartment;
}

export interface IValidateOrderKeyResponse {
  errors: string[] | null;
  warnings: string[] | null;
}

export interface IDepartmentBudgetSummariesResponse {
  department_budget_summaries: IDepartment[];
}

export interface IRemovePIIRequestPayload extends IPromiseCallbacks {
  from: string;
  to: string;
  orgId: string;
}

export interface IRunAnonymizationCheckRequestPayload {
  orgId: string;
  from: string;
  to: string;
}

export interface IRunCheckDateRange {
  from: string;
  to: string;
}

export interface IPendingAnonymizationCheck {
  orgId: string;
  baselineTimestamp: string | null;
  startedAt: string;
}

export interface IResumeAnonymizationCheckPayload {
  orgId: string;
  baselineTimestamp: string | null;
  startedAt: string;
}

export interface IUpdateAnonymizationLastRunStatusPayload {
  orgId: string;
  lastRunStatus: IAnonymizationLastRunStatus;
}

//#endregion
